import {
  PersonFormNodeConfigResponse,
  PersonFormsConfig,
  TabsConfigResponseForm,
} from "@interfaces/response";
import { BOFU_TABS_FORM_CODES } from "@helpers/constantes";
import {
  ALL_FIELDS_DISABLED,
  DEFAULT_PERSON_FORMS_CONFIG,
  PERSON_FORM_FIELD_CODES,
  PersonFormFieldCode,
  PersonFormFieldConfig,
} from "@components/PersonForm/personFormConstants";

const cloneDisabledFields = (): Record<PersonFormFieldCode, PersonFormFieldConfig> => ({
  ...ALL_FIELDS_DISABLED,
});

/**
 * Combina un campo del default con el del API.
 * Si el API no trae el campo, se usa el default (o deshabilitado si tampoco existe).
 * Si lo trae, `enabled` viene del API y `required` del API o, si no viene, del default.
 */
const mergeFieldConfig = (
  defaultField: PersonFormFieldConfig | undefined,
  apiField: PersonFormFieldConfig | undefined,
): PersonFormFieldConfig => {
  if (!apiField) {
    return defaultField ?? { enabled: false };
  }
  return {
    enabled: apiField.enabled,
    ...(apiField.required !== undefined
      ? { required: apiField.required }
      : defaultField?.required !== undefined
      ? { required: defaultField.required }
      : {}),
  };
};

/**
 * Combina un nodo (tipo de persona) del default con el del API.
 * Sin nodo del API regresa una copia del default. Con nodo: `enabled` del API,
 * `code` y `label` del API o del default si no vienen, y campos mergeados uno a uno.
 */
const mergeNodeConfig = (
  defaultNode: PersonFormNodeConfigResponse,
  apiNode: PersonFormNodeConfigResponse | undefined,
): PersonFormNodeConfigResponse => {
  if (!apiNode) {
    return {
      ...defaultNode,
      fields: { ...defaultNode.fields },
    };
  }

  const mergedFields: Record<string, PersonFormFieldConfig> = cloneDisabledFields();

  PERSON_FORM_FIELD_CODES.forEach((fieldCode) => {
    mergedFields[fieldCode] = mergeFieldConfig(defaultNode.fields[fieldCode], apiNode.fields?.[fieldCode]);
  });

  return {
    code: apiNode.code || defaultNode.code,
    label: apiNode.label ?? defaultNode.label,
    enabled: apiNode.enabled,
    fields: mergedFields,
  };
};

/**
 * Obtiene la config de formularios por persona a partir de `tabs_bofu_config`.
 * Sin FORMULARIOS_PERSONA o config vacía → default local.
 * Con config parcial del API → merge sobre el default (nodo y campo).
 * Nodos que solo existen en el API se agregan con sus campos sobre la base deshabilitada.
 */
export const resolvePersonFormsConfig = (forms?: Array<TabsConfigResponseForm>): PersonFormsConfig => {
  const formEntry = forms?.find((form) => form.code === BOFU_TABS_FORM_CODES.formularios_persona);
  const apiConfig = formEntry?.config;

  if (!apiConfig || Object.keys(apiConfig).length === 0) {
    return structuredClone(DEFAULT_PERSON_FORMS_CONFIG);
  }

  const resolved: PersonFormsConfig = {};

  Object.entries(DEFAULT_PERSON_FORMS_CONFIG).forEach(([nodeCode, defaultNode]) => {
    resolved[nodeCode] = mergeNodeConfig(defaultNode, apiConfig[nodeCode]);
  });

  Object.entries(apiConfig).forEach(([nodeCode, apiNode]) => {
    if (!resolved[nodeCode] && apiNode) {
      resolved[nodeCode] = {
        code: apiNode.code || nodeCode,
        label: apiNode.label,
        enabled: apiNode.enabled ?? false,
        fields: { ...cloneDisabledFields(), ...(apiNode.fields || {}) },
      };
    }
  });

  return resolved;
};

/**
 * Calcula los campos a mostrar/validar para una persona según sus tipos.
 *
 * Capas:
 * 1. `formsConfig` ya debe venir de `resolvePersonFormsConfig` (API mergeado sobre default local por nodo).
 *    Si no se pasa o está vacío, se usa `DEFAULT_PERSON_FORMS_CONFIG`.
 * 2. Unión entre los tipos de la misma persona (p. ej. PTRL + PTAM) con OR:
 *    - `enabled` = true si algún nodo habilitado lo tiene enabled
 *    - `required` = true si algún nodo habilitado lo marca required
 *
 * Ejemplo PTRL + PTAM (defaults): celular/email/rfc del PTRL + ocupación/estado civil/régimen/cónyuge del PTAM.
 * Codes sin nodo en `formsConfig` se ignoran. Nodos con `enabled: false` no aportan campos.
 */
export const resolveFieldsForPerson = (
  personTypeCodes: string[],
  formsConfig?: PersonFormsConfig | null,
): Record<PersonFormFieldCode, PersonFormFieldConfig> => {
  if (!personTypeCodes.length) {
    return cloneDisabledFields();
  }

  const config = formsConfig && Object.keys(formsConfig).length > 0 ? formsConfig : DEFAULT_PERSON_FORMS_CONFIG;
  const resolved = cloneDisabledFields();

  const knownTypeCodes = personTypeCodes.filter((typeCode) => Boolean(config[typeCode]));

  knownTypeCodes.forEach((typeCode) => {
    const node = config[typeCode];
    if (!node || node.enabled === false) {
      return;
    }

    PERSON_FORM_FIELD_CODES.forEach((fieldCode) => {
      const field = node.fields?.[fieldCode];
      if (!field?.enabled) {
        return;
      }

      resolved[fieldCode] = {
        enabled: true,
        required: Boolean(resolved[fieldCode].required || field.required),
      };
    });
  });

  return resolved;
};

/** Indica si hay al menos un campo habilitado para renderizar el formulario. */
export const hasEnabledPersonFields = (fields: Record<PersonFormFieldCode, PersonFormFieldConfig>): boolean =>
  PERSON_FORM_FIELD_CODES.some((code) => fields[code]?.enabled);
